import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

// Strip blank lines + comments, keep only entries that look like a YouTube
// URL. Lightweight client-side filter so the user gets immediate "N URLS
// DETECTED" feedback before they hit submit; the backend still does the
// authoritative validation.
const YT_HINT = /(youtube\.com|youtu\.be|youtube-nocookie\.com)/i;

function parseUrls(raw) {
  if (!raw) return [];
  return raw
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith("#"))
    .filter((line) => YT_HINT.test(line));
}

export default function YouTubeIngest({
  apiBase,
  authHeaders,
  channels,
  selectedChannel,
  busy,
  setBusy,
  append,
  onUploaded,
  onTuneToChannel,
}) {
  const [ytText, setYtText] = useState("");
  // Per-URL pre-check results, keyed by URL. Each value is the same shape
  // /api/youtube/check returns ({ok, reason, title, yt_id}). Empty until the
  // owner clicks VALIDATE — so paste-and-submit still works in one click
  // and we don't burn quota on every keystroke.
  const [checks, setChecks] = useState({});
  const [checking, setChecking] = useState(false);

  const urls = useMemo(() => parseUrls(ytText), [ytText]);

  // Clear stale validation results whenever the URL list changes — old
  // verdicts attached to URLs the owner just edited would be misleading.
  // We compare by URL so deletes/adds don't wipe the whole map.
  useEffect(() => {
    setChecks((prev) => {
      const next = {};
      for (const url of urls) {
        if (prev[url]) next[url] = prev[url];
      }
      return next;
    });
  }, [urls]);

  const handleValidate = async () => {
    if (urls.length === 0) return;
    setChecking(true);
    append(`> PRE-CHECKING ${urls.length} URL${urls.length === 1 ? "" : "S"} VIA YOUTUBE DATA API...`);
    let ok = 0;
    let bad = 0;
    const next = { ...checks };
    for (let i = 0; i < urls.length; i += 1) {
      const url = urls[i];
      try {
        const r = await axios.post(
          `${apiBase}/youtube/check`,
          { url },
          { headers: { "Content-Type": "application/json", ...authHeaders() } }
        );
        next[url] = r.data;
        if (r.data?.ok) ok += 1;
        else bad += 1;
      } catch (e) {
        next[url] = { ok: false, reason: e.response?.data?.detail || e.message };
        bad += 1;
      }
      setChecks({ ...next });
    }
    append(`> DONE. ${ok} OK${bad ? `, ${bad} BLOCKED` : ""}.`);
    setChecking(false);
  };

  const handleAddYouTube = async () => {
    if (!selectedChannel || urls.length === 0) {
      append("> ERROR: SELECT A CHANNEL AND PASTE AT LEAST ONE YOUTUBE URL");
      return;
    }
    setBusy(true);
    // Sub-channels are derived — proxy the ingest to the parent's real
    // channel id. Tape lands on the parent; the sub picks it up
    // automatically once its show_title matches.
    const selectedCh = channels.find((c) => c.id === selectedChannel);
    const isSubChannel = !!selectedCh?.isSubChannel;
    const effectiveChannelId = isSubChannel ? selectedCh.parent_id : selectedChannel;
    const chNumber = channels.find((c) => c.id === effectiveChannelId)?.number;
    append(`> LINKING ${urls.length} YOUTUBE TAPE${urls.length === 1 ? "" : "S"} TO CH ${chNumber}...`);

    // Sequential POSTs. Channels store tapes in `order`, and the backend
    // appends each one to the end — keeping the loop sequential preserves
    // the user's paste order on the channel.
    let okCount = 0;
    let failCount = 0;
    for (let i = 0; i < urls.length; i += 1) {
      const url = urls[i];
      const prefix = urls.length > 1 ? `[${i + 1}/${urls.length}] ` : "";
      try {
        await axios.post(
          `${apiBase}/channels/${effectiveChannelId}/youtube`,
          { url, title: null },
          { headers: { "Content-Type": "application/json", ...authHeaders() } }
        );
        append(`> ${prefix}OK — ${url}`);
        okCount += 1;
      } catch (e) {
        const reason = e.response?.data?.detail || e.message;
        append(`> ${prefix}FAILED — ${url} (${reason})`);
        failCount += 1;
      }
    }
    append(
      `> DONE. ${okCount} LINKED${failCount ? `, ${failCount} FAILED` : ""}.`
    );

    if (okCount > 0) {
      setYtText("");
      await onUploaded(effectiveChannelId);
      if (onTuneToChannel) onTuneToChannel(effectiveChannelId);
    }
    setBusy(false);
  };

  const _selectedCh = channels.find((c) => c.id === selectedChannel);
  const _effectiveId = _selectedCh?.isSubChannel ? _selectedCh.parent_id : selectedChannel;
  const chLabel = channels.find((c) => c.id === _effectiveId)?.number ?? "?";

  return (
    <div className="border border-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] p-2" data-testid="youtube-panel">
      <div className="text-[11px] opacity-80 mb-1">&gt; LINK YOUTUBE TAPES (NO UPLOAD):</div>
      <div className="flex flex-col gap-1.5">
        <textarea
          value={ytText}
          onChange={(e) => setYtText(e.target.value)}
          placeholder={"https://www.youtube.com/watch?v=...\nhttps://youtu.be/...\nhttps://www.youtube.com/watch?v=...\n# Paste one URL per line"}
          data-testid="youtube-url-input"
          rows={5}
          className="w-full bg-black border border-[var(--phosphor)] text-[var(--phosphor)] p-1.5 text-[11px] outline-none placeholder:text-[color-mix(in_srgb,var(--phosphor)_40%,transparent)] font-mono resize-y"
        />
        <div className="flex items-center justify-between text-[10px] opacity-70 px-0.5">
          <span data-testid="youtube-url-count">
            {urls.length === 0
              ? "> PASTE URLS (ONE PER LINE)"
              : `> ${urls.length} URL${urls.length === 1 ? "" : "S"} DETECTED`}
          </span>
          {urls.length > 0 && (
            <button
              type="button"
              onClick={() => { setYtText(""); setChecks({}); }}
              className="opacity-60 hover:opacity-100 underline decoration-dotted"
              data-testid="youtube-clear"
            >
              CLEAR
            </button>
          )}
        </div>
        {Object.keys(checks).length > 0 && (
          <div
            className="border border-[color-mix(in_srgb,var(--phosphor)_30%,transparent)] bg-black/30 max-h-32 overflow-y-auto scrollbar-crt text-[10px] leading-snug"
            data-testid="youtube-check-results"
          >
            {urls.map((url, i) => {
              const c = checks[url];
              if (!c) return null;
              return (
                <div
                  key={`${url}-${i}`}
                  className={`flex items-center gap-2 px-1.5 py-0.5 border-b border-[color-mix(in_srgb,var(--phosphor)_10%,transparent)] last:border-b-0 ${c.ok ? "text-[var(--phosphor)]" : "text-[#FFCC00]"}`}
                  data-testid={`youtube-check-row-${i}`}
                >
                  <span className="shrink-0 font-bold">
                    {c.ok ? "OK" : "✕"}
                  </span>
                  <span className="truncate flex-1">
                    {c.title || url}
                  </span>
                  {!c.ok && (
                    <span className="shrink-0 opacity-90 uppercase tracking-wider">
                      {c.reason || "BLOCKED"}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
        <div className="flex gap-1.5">
          <button
            onClick={handleValidate}
            disabled={busy || checking || urls.length === 0}
            data-testid="youtube-validate"
            type="button"
            className="border border-[#FFCC00] text-[#FFCC00] py-1 px-2 text-[11px] hover:bg-[#FFCC00] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-bold tracking-widest"
            title="Check embeddability via YouTube Data API before adding"
          >
            {checking ? "CHECKING…" : "VALIDATE"}
          </button>
          <button
            onClick={handleAddYouTube}
            disabled={busy || urls.length === 0 || !selectedChannel}
            data-testid="youtube-add-button"
            className="flex-1 border border-[var(--phosphor)] py-1 text-[11px] hover:bg-[var(--phosphor)] hover:text-black transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-bold tracking-widest"
          >
            LINK {urls.length > 0 ? urls.length : ""} TAPE{urls.length === 1 ? "" : "S"} TO CH {chLabel}
          </button>
        </div>
        <div className="text-[9px] opacity-60">
          One URL per line. VALIDATE runs a free pre-check (YouTube Data API) so blocked tapes are caught before linking.
        </div>
      </div>
    </div>
  );
}
